import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import { rem } from 'polished';
import { FlexRow } from '../../Defaults/Flexbox/Flexbox';
import { __ } from 'react-i18n';
import { PaginationSimple } from '../../..';
import { SvgIcon, IconType } from '../General/SvgIcon';
import { CheckBox } from '../General/Checkbox';
import { WishlistProductRow } from './WishlistProductRow';
import { ButtonPrimary } from '../General/Button';
import { Loader } from '../Cart/Summary/ThirdStep';

interface Props {
  products: any[];
  addToCart: (goodId: any, count: number) => void;
  addBulkToCart: (products: { [key: string]: number }) => void;
  removeProductFromWishList: (productId: number, goodId: number) => void;
  total: number;
  limit: number;
  offset: number;
  handleOffsetChange;
  addingToCart: boolean;
}
function ClubWishList({
  products,
  addToCart,
  addBulkToCart,
  removeProductFromWishList,
  total,
  limit,
  offset,
  handleOffsetChange,
  addingToCart,
}: Props) {
  const [selectedProducts, setSelectedProducts] = React.useState<{
    [key: string]: number;
  }>({});

  const handleChange = (goodId: string, quantity: number, checked: boolean) => {
    setSelectedProducts(prev => {
      if (checked) {
        return { ...prev, [goodId]: quantity };
      } else {
        if (selectedAll) {
          setselectedAll(false);
        }
        const { [goodId]: _, ...rest } = prev;
        return rest;
      }
    });
  };
  const [selectedAll, setselectedAll] = React.useState(false);
  const handleSelectAllChange = () => {
    setselectedAll(!selectedAll);
    if (!selectedAll) {
      const newSelectedProducts = products.reduce(
        (acc, product) => ({
          ...acc,
          [product.good_id]: counts[product.good_id] || 1,
        }),
        {},
      );
      setSelectedProducts(newSelectedProducts);
    } else {
      setSelectedProducts({});
    }
  };
  const [counts, setCounts] = React.useState({});

  React.useEffect(() => {
    if (selectedAll) {
      const newSelectedProducts = products.reduce(
        (acc, product) => ({
          ...acc,
          [product.good_id]: counts[product.good_id] || 1,
        }),
        {},
      );
      setSelectedProducts(newSelectedProducts);
    }
  }, [counts, selectedAll, products]);

  const handleSetCount = productId => count => {
    setCounts(prevCounts => ({ ...prevCounts, [productId]: count }));
    setSelectedProducts(prevSelectedProducts => ({
      ...prevSelectedProducts,
      [productId]: count,
    }));
  };
  React.useEffect(() => {
    setSelectedProducts({});
  }, [setSelectedProducts]);

  const wishListProducts =
    products &&
    products.map((product: any) => (
      <WishlistProductRow
        key={product.good_id}
        product={product}
        addToCart={addToCart}
        selected={selectedProducts[product.good_id] !== undefined}
        onCheckboxChange={handleChange}
        count={counts[product.good_id] || 1}
        setCount={handleSetCount(product.good_id)}
        handleRemoveClick={removeProductFromWishList}
      />
    ));

  const handleAddBulkToCart = () => {
    addBulkToCart(selectedProducts);
  };

  return (
    <Wrapper>
      <>
        <SelectAllWrapper>
          <CheckBox
            wrapperStyle={{ marginBottom: 0, fontSize: rem(16) }}
            label={__('Označiť všetko')}
            type={'checkbox'}
            onClick={handleSelectAllChange}
            name="selectAll"
            selected={Object.keys(selectedProducts).length === products?.length}
            halfSelected={
              Object.keys(selectedProducts).length > 0 &&
              Object.keys(selectedProducts).length !== products?.length
            }
          />
          {addingToCart ? (
            <LoadingWrapper>
              <Loader />
            </LoadingWrapper>
          ) : null}
        </SelectAllWrapper>
        {wishListProducts}
        <BottomPartWrapper>
          <AddToCartButton
            onClick={handleAddBulkToCart}
            disabled={
              addingToCart || Object.keys(selectedProducts).length === 0
            }
          >
            <SvgIcon
              width={24}
              height={24}
              icon={IconType.newCart}
              fill={'#FFFFFF'}
              alt={__('Košík')}
              title={__('Košík')}
              marginRight={12}
              cursor={'pointer'}
            />
            {addingToCart ? __('Vkladám do košíka..') : __('Vložiť označené')}
          </AddToCartButton>{' '}
          <PaginationSimple
            totalItems={total}
            limit={limit}
            offset={offset}
            handleOffsetChange={handleOffsetChange}
          />
        </BottomPartWrapper>
      </>
    </Wrapper>
  );
}

const LoadingWrapper = styled.div`
  width: 50px;
  height: 50px;
  margin-right: ${rem(16)};
`;
const animateIn = keyframes`
   0% {
    opacity: 0;
    transform: scale(0.6) translateY(-8px);
  }
  
  100% {
    opacity: 1;
  }
`;

const Wrapper = styled(FlexRow)`
  flex-flow: row wrap;
  animation: ${animateIn} 0.2s ease;
  position: relative;

  ${({ theme }) => theme.mediab.m810`
       margin: 0;
      margin-bottom: ${({ visible }) => (visible ? rem(32) : 0)};
  `};
`;

const SelectAllWrapper = styled(FlexRow)`
  border-top: 1px solid #e8e8e8;
  border-bottom: 1px solid #e8e8e8;
  height: ${rem(80)};
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BottomPartWrapper = styled(FlexRow)`
  display: flex;
  justify-content: space-between;
  width: calc(100% + ${rem(8)});
  margin-top: ${rem(24)};
  display: flex;
  flex-flow: row wrap;
`;
const AddToCartButton = styled(ButtonPrimary)`
  font-family: ${({ theme }) => theme.font.primary};
  text-transform: none;
  font-weight: 600;
  font-size: ${rem(16)};
  height: ${rem(48)};

  &:disabled {
    background-color: #dcd6d6;
    color: ${({ theme }) => theme.colors.black};

    svg {
      fill: ${({ theme }) => theme.colors.black};

      * {
        fill: inherit;
      }
    }
  }
  ${({ theme }) => theme.mediab.m810`
    margin-bottom: ${rem(16)};
  `};

  ${({ theme }) => theme.mediab.s450`
    margin: ${rem(24)} auto;
  `}
`;

export { ClubWishList };
